import CoachReviewModal from "@/Components/UI/Modals/CoachReviewModal";
import { states } from "@/constants/geography";
import { PageProps } from "@/types/page";
import type { ImpactLevel } from "@/types/VideoData";
import { consumeUrlParam } from "@/utils/UrlParams";
import { usePage } from "@inertiajs/react";
import { AxiosError } from "axios";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { MdOutlineRateReview } from "react-icons/md";
import {
    PiCalendarDots,
    PiCertificate,
    PiMapPin,
    PiStar,
} from "react-icons/pi";
import { CoachInfoSubcomponentProps, TopItemsAndOptions } from "../CoachInfo";
import AvailabilityModal from "./AvailabilityModal";

type ReviewRequest = {
    shouldShow: boolean;
    reviewId: number | null;
    impactLevel: ImpactLevel | null;
};

export default function CoachInfoHeader({
    coachInfoData,
    config,
}: CoachInfoSubcomponentProps) {
    const { user } = usePage<PageProps>().props;

    const { coach, appointmentReviewStats } = coachInfoData ?? {};
    const { bigCenteredProfilePhoto } = config;

    const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [reviewRequest, setReviewRequest] = useState<ReviewRequest | null>(
        null,
    );
    const [openedFromReminderEmail, setOpenedFromReminderEmail] =
        useState(false);

    const componentMap: Record<TopItemsAndOptions, React.ReactNode> = {
        license: (
            <div key="license" className="flex items-center gap-2">
                <PiCertificate className="min-w-fit" />
                <div>{coach?.certifications}</div>
            </div>
        ),
        location: (
            <div key="location" className="flex items-center gap-2">
                <PiMapPin className="min-w-fit" />
                <div>
                    {coach?.coach_details?.state
                        ? `${states[coach.coach_details?.state]}, `
                        : ""}
                    {coach?.coach_details?.country}
                </div>
            </div>
        ),
        ratings:
            appointmentReviewStats?.averageRating &&
            appointmentReviewStats.totalReviews >= 3 ? (
                <div key="ratings" className="flex items-center gap-2">
                    <PiStar className="min-w-fit" />
                    <div>
                        {appointmentReviewStats?.averageRating.toPrecision(2)}
                        /10 ({appointmentReviewStats.totalReviews} session
                        ratings)
                    </div>
                </div>
            ) : null,
        viewAvailability: (
            <button
                key="viewAvailability"
                className="flex items-center gap-2"
                onClick={() => setShowAvailabilityModal(true)}
            >
                <PiCalendarDots className="min-w-fit" />
                <div className="text-brand-red underline">
                    View {coach?.first_name}'s availability
                </div>
            </button>
        ),
        submitRating: reviewRequest?.shouldShow ? (
            <button
                key="submitRating"
                className="flex items-center gap-2"
                onClick={() => setShowReviewModal(true)}
            >
                <MdOutlineRateReview className="min-w-fit" />
                <div className="text-brand-red underline">Write a review</div>
            </button>
        ) : null,
    };

    const getUnansweredReviewRequest = async (
        andOpenModal: boolean = false,
    ) => {
        try {
            const { data } = await window.axios.get<ReviewRequest>(
                route("getUnansweredReviewRequest", coach?.id),
            );
            setReviewRequest(data);
            if (data?.shouldShow && andOpenModal) {
                setShowReviewModal(true);
            }
        } catch (e) {
            const err = e as AxiosError;
            if (err.status !== 404) {
                throw e;
            }
            // Got 404, no review request
            setReviewRequest(null);
        }
    };

    useEffect(() => {
        if (
            coachInfoData &&
            config.topItemsAndOptions.includes("submitRating")
        ) {
            const shouldAutoOpen = consumeUrlParam("openReviewModal") === "1";
            if (shouldAutoOpen) {
                setOpenedFromReminderEmail(true);
            }
            getUnansweredReviewRequest(shouldAutoOpen);
        }
    }, [coachInfoData, config.topItemsAndOptions]);

    return !coachInfoData ? (
        <CoachInfoSkeleton config={config} />
    ) : (
        <div
            className={classNames(
                "mt-2 flex gap-3",
                bigCenteredProfilePhoto === true
                    ? "flex-col items-center"
                    : bigCenteredProfilePhoto === false
                      ? "justify-start"
                      : "flex-col items-center @sm/coach-info:items-start @sm/coach-info:flex-row @sm/coach-info:justify-start",
            )}
        >
            <AvailabilityModal
                show={showAvailabilityModal}
                setShow={setShowAvailabilityModal}
                coach={coachInfoData.coach}
                onSecondBiteSelect={config.switchCoachButton}
            />
            {reviewRequest?.shouldShow && (
                <CoachReviewModal
                    key={`coach-review-${showReviewModal}`}
                    showModal={showReviewModal}
                    onRequestClose={() => {
                        setShowReviewModal(false);
                        setOpenedFromReminderEmail(false);
                        getUnansweredReviewRequest();
                    }}
                    coach={coachInfoData.coach}
                    existingReviewId={reviewRequest.reviewId}
                    initialImpactLevel={reviewRequest.impactLevel}
                    submissionSource={
                        openedFromReminderEmail
                            ? "reminder_email"
                            : "session_6_modal"
                    }
                />
            )}
            <div
                className={classNames(
                    "rounded-full bg-contain bg-center bg-no-repeat ring-1 ring-brand-border aspect-square",
                    bigCenteredProfilePhoto === true
                        ? "w-40 h-40"
                        : bigCenteredProfilePhoto === false
                          ? "w-24 h-24"
                          : "w-40 h-40 @sm/coach-info:w-24 @sm/coach-info:h-24",
                )}
                style={{
                    backgroundImage: `url(${coachInfoData.coach.profile_photo_url})`,
                }}
            />
            <div
                className={classNames(
                    "flex flex-col justify-center",
                    bigCenteredProfilePhoto === true
                        ? "items-center"
                        : bigCenteredProfilePhoto === false
                          ? "items-start"
                          : "items-center @sm/coach-info:items-start",
                )}
            >
                <div
                    className={classNames(
                        "flex flex-wrap gap-3 items-center",
                        bigCenteredProfilePhoto === true
                            ? "justify-center"
                            : bigCenteredProfilePhoto === false
                              ? "justify-start"
                              : "justify-center @sm/coach-info:justify-start",
                    )}
                >
                    <div
                        data-testid="counterpart-info"
                        className="text-2xl font-bold mt-2 lg:mt-0"
                    >
                        <div>
                            {/**
                             * This last name logic is correct. Clients already get the truncated last name
                             * in the CoachResource. We want coaches to see the same thing as the clients
                             * so we're manually truncating just for coaches here.
                             */}
                            {coachInfoData.coach.first_name}{" "}
                            {user?.type === "coach"
                                ? coachInfoData.coach.last_name.slice(0, 1) +
                                  "."
                                : coachInfoData.coach.last_name}
                        </div>
                    </div>
                    {config.nameBadgePlacement === "besideName" &&
                        config.nameBadge}
                </div>
                {config.nameBadgePlacement === "aboveTopItems" &&
                    !!config.nameBadge && (
                        <div className="my-1 flex">{config.nameBadge}</div>
                    )}

                <div
                    className={classNames(
                        "flex flex-col text-brand-black text-base font-normal",
                        bigCenteredProfilePhoto === true
                            ? "gap-2 mt-2"
                            : bigCenteredProfilePhoto === false
                              ? "gap-0"
                              : "gap-2 mt-2 @sm/coach-info:gap-0 @sm/coach-info:mt-0",
                    )}
                >
                    {config.topItemsAndOptions.map(
                        (option) => componentMap[option],
                    )}
                </div>
            </div>
        </div>
    );
}

function CoachInfoSkeleton({
    config,
}: Pick<CoachInfoSubcomponentProps, "config">) {
    const { bigCenteredProfilePhoto } = config;

    return (
        <div
            className={classNames(
                "mt-2 flex gap-3",
                bigCenteredProfilePhoto === true
                    ? "flex-col items-center"
                    : bigCenteredProfilePhoto === false
                      ? "justify-start"
                      : "flex-col items-center @sm/coach-info:items-start @sm/coach-info:flex-row @sm/coach-info:justify-start",
            )}
        >
            <div
                className={classNames(
                    "rounded-full bg-brand-lightGray animate-pulse",
                    bigCenteredProfilePhoto === true
                        ? "w-40 h-40"
                        : bigCenteredProfilePhoto === false
                          ? "w-24 h-24"
                          : "w-40 h-40 @sm/coach-info:w-24 @sm/coach-info:h-24",
                )}
            />
            <div
                className={classNames(
                    "flex flex-col justify-center",
                    bigCenteredProfilePhoto === true
                        ? "items-center"
                        : bigCenteredProfilePhoto === false
                          ? "items-start"
                          : "items-center @sm/coach-info:items-start",
                )}
            >
                <div className="mt-2 text-2xl font-bold lg:mt-0">
                    <div className="h-8 w-48 bg-brand-lightGray animate-pulse rounded-md" />
                </div>

                <div
                    className={classNames(
                        "flex flex-col text-brand-black text-lg font-normal",
                        bigCenteredProfilePhoto === true
                            ? "gap-2 mt-2"
                            : bigCenteredProfilePhoto === false
                              ? "gap-0"
                              : "gap-2 mt-2 @sm/coach-info:gap-0 @sm/coach-info:mt-0",
                    )}
                >
                    {config.topItemsAndOptions.map((option) => (
                        <div
                            key={option}
                            className="flex items-center gap-2 pt-1"
                        >
                            <div className="w-5 h-5 bg-brand-lightGray animate-pulse rounded" />
                            <div className="h-5 w-32 bg-brand-lightGray animate-pulse rounded" />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
